/**
 * SessionMessageRenderer
 * Lightweight read-only message renderer for session history display.
 * Produces HTML using the .session-messages classes in sessions.css.
 *
 * Unlike the live chat (BlockStreamBuffer / MessageRenderer) and the session
 * resume path (ChatManager.loadHistoryMessages → MessageList.setMessageText),
 * this renderer is fully STATIC and NON-INTERACTIVE: embedded structured cards
 * render with their visual content but emit no listeners, drill-ins, chevrons,
 * CTAs, or live-coordination attributes. See structuredBlockRenderers.js
 * `{ interactive: false }`.
 *
 * @module components/SessionMessageRenderer
 */

import { renderInterleavedBlocks, REPLAY_SKIP_TAGS } from '../chat/structuredBlockRenderers.js';
import { classifyUserReplayMessage } from '../chat/messageReplayPolicy.js';
import { appendStoppedIndicator } from '../chat/MessageList.js';

/**
 * Renders session messages in a read-only format for the history detail view.
 */
export class SessionMessageRenderer {
  /**
   * Render a list of messages in read-only format. Applies the same metadata
   * filtering the live resume path uses so synthetic/system markers and
   * non-echo button clicks don't appear as literal text.
   * @param {Array} messages - Array of message objects with role, content, metadata, createdAt
   * @returns {HTMLElement} Container with rendered messages
   */
  static render(messages) {
    const container = document.createElement('div');
    container.className = 'session-messages';

    if (!Array.isArray(messages)) return container;

    for (const msg of messages) {
      const messageEl = this.renderMessage(msg);
      if (!messageEl) continue;

      container.appendChild(messageEl);
      if (msg.role === 'user') {
        const decision = classifyUserReplayMessage(msg);
        if (decision.dividerAfter) {
          container.appendChild(this.buildDivider(decision.dividerAfter));
        }
      }
    }

    return container;
  }

  /**
   * Render a single message, or return null if it should be hidden.
   *
   * User-message filtering is delegated to the shared {@link classifyUserReplayMessage}
   * policy, also used by the live resume path (ChatManager.loadHistoryMessages),
   * so the two can't drift.
   *
   * @param {Object} message - Message object with role, content, metadata
   * @returns {HTMLElement|null} Message element, or null when filtered out
   */
  static renderMessage(message) {
    if (!message) return null;
    if (message.role === 'assistant' && message.metadata?.supersededByRetry === true) return null;

    if (message.role === 'user') {
      const decision = classifyUserReplayMessage(message);
      if (decision.skip) return null;
      return this.buildUserMessage(decision.text, message.createdAt);
    }

    // Assistant (and any system) messages render as static assistant bubbles.
      const element = this.buildAssistantMessage(message.content, message.createdAt);
      if (message.metadata?.stoppedByUser === true) appendStoppedIndicator(element);
      return element;
  }

  /**
   * Format a message's clock time for the speaker label ("2:14 PM").
   * @param {string|Date|undefined} createdAt - Message timestamp
   * @returns {string} Formatted time, or '' when absent or not a valid date
   */
  static formatMessageTime(createdAt) {
    if (createdAt === undefined || createdAt === null || createdAt === '') return '';
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  /**
   * Build the speaker label above a turn (eyebrow voice): the speaker name,
   * then — only when the message carries a valid `createdAt` — a middle dot
   * and the clock time in `.message__time`. The time lives here, never inside
   * `.message__content`, so a bubble's textContent stays exactly the message.
   * @param {string} name - Speaker name ("You" / "Encouple")
   * @param {string|Date|undefined} createdAt - Message timestamp
   * @returns {HTMLElement}
   */
  static buildSpeakerLabel(name, createdAt) {
    const speaker = document.createElement('span');
    speaker.className = 'message__speaker';
    speaker.textContent = name;

    const time = this.formatMessageTime(createdAt);
    if (time) {
      const timeEl = document.createElement('span');
      timeEl.className = 'message__time';
      timeEl.textContent = ` · ${time}`;
      speaker.appendChild(timeEl);
    }
    return speaker;
  }

  /**
   * Build a static user-message turn: a "You" speaker label (eyebrow voice,
   * matching the rest of the detail-page system, with the turn's clock time)
   * above a plain-text message.
   * @param {string} text
   * @param {string|Date} [createdAt] - Message timestamp for the label's time
   * @returns {HTMLElement}
   */
  static buildUserMessage(text, createdAt) {
    const turn = document.createElement('div');
    turn.className = 'message-turn message-turn--user';
    turn.appendChild(this.buildSpeakerLabel('You', createdAt));

    const wrapper = document.createElement('div');
    wrapper.className = 'message message--user';

    const content = document.createElement('div');
    content.className = 'message__content';

    const p = document.createElement('p');
    p.textContent = text || '';
    content.appendChild(p);

    wrapper.appendChild(content);
    turn.appendChild(wrapper);
    return turn;
  }

  /**
   * Build a static topic-transition divider.
   * @param {string} text
   * @returns {HTMLElement}
   */
  static buildDivider(text) {
    const divider = document.createElement('div');
    divider.className = 'message message--divider';

    const label = document.createElement('span');
    label.className = 'message__divider-text';
    label.textContent = text;
    divider.appendChild(label);

    return divider;
  }

  /**
   * Build a static assistant-message turn: an "Encouple" speaker label above
   * the message, rehydrating any embedded structured cards interleaved with
   * prose. The serif face on `.message__content` (sessions.css) is the other
   * half of the speaker distinction, paired against the user's sans.
   * @param {string} content
   * @param {string|Date} [createdAt] - Message timestamp for the label's time
   * @returns {HTMLElement}
   */
  static buildAssistantMessage(content, createdAt) {
    const turn = document.createElement('div');
    turn.className = 'message-turn message-turn--assistant';
    turn.appendChild(this.buildSpeakerLabel('Encouple', createdAt));

    const wrapper = document.createElement('div');
    wrapper.className = 'message message--assistant';

    const contentEl = document.createElement('div');
    contentEl.className = 'message__content';
    contentEl.appendChild(this.renderAssistantBody(content));

    wrapper.appendChild(contentEl);
    turn.appendChild(wrapper);
    return turn;
  }

  /**
   * Walk a stored assistant message, rendering every embedded structured block
   * (e.g. <insight>, <practice-reference>, <l2-closing-summary>) as a static,
   * non-interactive card, interleaved with the surrounding prose. Delegates to
   * the shared {@link renderInterleavedBlocks} walker (also used by the live
   * chat poll/reload path) so the two can't drift — transcript replay passes
   * `interactive: false` and skips client-only lifecycle cards.
   *
   * @param {string} content - Raw stored assistant content
   * @returns {DocumentFragment}
   */
  static renderAssistantBody(content) {
    return renderInterleavedBlocks(content, { interactive: false, skip: REPLAY_SKIP_TAGS });
  }

  /**
   * Render messages into an existing container
   * @param {HTMLElement} container - Container element to render into
   * @param {Array} messages - Array of message objects
   */
  static renderInto(container, messages) {
    container.innerHTML = '';
    const rendered = this.render(messages);
    container.appendChild(rendered);
  }
}
